<template>
  <b-container>
    <b-row class="mb-2 tabHeader">
      <b-col cols="12" class="font-weight-bold" v-t="'productDetails.tabHeader.performanceIndicator'"></b-col>
    </b-row>
    <b-row>
      <b-col cols="12">
        <b-table striped hover :items="items" :fields="fields" class="results w-100">
          <template v-slot:cell(label)="{ value }"> {{ value }} </template>
          <template v-slot:cell(min)="row">
            {{ percentageFields.includes(row.item.key) ? formatPercentage(row.value) : formatDecimal(row.value) }}
          </template>
          <template v-slot:cell(max)="row">
            {{ percentageFields.includes(row.item.key) ? formatPercentage(row.value) : formatDecimal(row.value) }}
          </template>
        </b-table>
      </b-col>
    </b-row>
    <template v-if="hasBreakEven">
      <b-row class="mb-2 tabHeader">
        <b-col cols="12" class="font-weight-bold" v-t="'productDetails.tabHeader.breakEvenRHP'"></b-col>
      </b-row>
      <template v-if="breakEvenOptions.length > 0">
        <b-row v-if="isMop">
          <b-col cols="12">
            {{ $t('productDetails.confidenceLevel') }}
            <b-select v-model="breakEventPercentage" :options="breakEvenOptions" size="sm" class="w-auto"></b-select>
          </b-col>
        </b-row>
      </template>
      <b-row class="mt-2">
        <b-col cols="12">
          <highcharts v-if="breakEven" :key="hcKey" :options="chartOptions"></highcharts>
        </b-col>
      </b-row>
    </template>
  </b-container>
</template>
<script>
import { rhpToYears, setChartMenuItemsLang } from '../../utils/formatter'

export default {
  name: 'product-performance',
  props: {
    value: {},
    isMop: { type: Boolean, default: false },
  },
  data() {
    return {
      hcKey: 0,
      breakEventPercentage: '50',
      percentageFields: [
        'vev',
        'valueAtRisk',
        'sharpeRationOnRiskFree',
        'percentageModerateGross',
        'normalizedReturn',
        'riyGrossReturn',
      ],
    }
  },
  methods: {
    sortPoints(el1, el2) {
      if (el1[0] > el2[0]) return 1
      if (el1[0] < el2[0]) return -1
      //equals case check second elements
      if (el1[1] > el2[1]) return 1
      if (el1[1] < el2[1]) return -1
      return 0
    },
    formatPercentage(val) {
      return val !== undefined && val !== null && (val !== '' || val === 0) && !isNaN(val)
        ? this.$n(val, 'percent')
        : '-'
    },
    formatDecimal(val) {
      return val !== undefined && val !== null && (val !== '' || val === 0) && !isNaN(val)
        ? this.$n(val, 'decimal')
        : '-'
    },
  },
  computed: {
    breakEven: function () {
      return this.value.breakEven
    },
    // The break-even section is drawn only when the backend sends data for it: an asset class whose
    // break-even indicators are not available gets an empty list (SOP) or nothing (MOP).
    hasBreakEven: function () {
      if (this.breakEven == null) return false
      return Array.isArray(this.breakEven) ? this.breakEven.length > 0 : Object.keys(this.breakEven).length > 0
    },
    performance: function () {
      return this.value.performance
    },
    rhp: function () {
      return this.value.rhp
    },
    items: function () {
      let ret = []
      if (this.performance.min != null) {
        Object.keys(this.performance.min).forEach((el) => {
          let row = {}
          row.key = el
          row.label = this.$t('search.resultTable.' + el)
          row.min = this.performance.min[el]
          if (this.isMop) {
            row.max = this.performance.max[el]
          }
          ret.push(row)
        })
      }
      return ret
    },
    fields: function () {
      let ret = [
        {
          key: 'label',
          label: this.$t('common.indicator'),
          class: 'text-left',
        },
        {
          key: 'empty',
          label: '',
        },
      ]
      if (this.isMop) {
        ret.push(
          {
            key: 'min',
            label: 'Min',
            class: 'text-right',
          },
          {
            key: 'max',
            label: 'Max',
            class: 'text-right',
          }
        )
      } else {
        ret.push(
          {
            key: 'empty2',
            label: '',
          },
          {
            key: 'min',
            label: '',
            class: 'text-right',
          }
        )
      }
      return ret
    },
    breakEvenOptions: function () {
      if (!this.isMop || this.breakEven == null) return []
      return Object.keys(this.breakEven).map((el) => el.replace('confidence', ''))
    },
    chartOptions: function () {
      return {
        lang: this.setOptions,
        chart: {
          type: this.isMop ? 'column' : 'line',
          backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--primaryBGColor'),
          height: '300px',
          marginTop: 50,
        },
        legend: {
          enabled: false,
        },
        colors: [getComputedStyle(document.documentElement).getPropertyValue('--secondary')],
        tooltip: {
          enabled: false,
        },
        xAxis: {
          categories: [],
          crosshair: true,
          title: {
            text: this.isMop
              ? this.$tc('productDetails.performanceChartLabels.breakEvenHorizon', this.breakEventPercentage)
              : this.$t('productDetails.performanceChartLabels.breakEvenRHP'),
          },
          max: 20,
          min: 1,
          labels: {
            formatter: (val) => rhpToYears(val.value),
          },
          plotLines: [
            {
              color: getComputedStyle(document.documentElement).getPropertyValue('--primary'), // Color value
              label: {
                align: 'left', // Positioning of the label.
                text: 'KID RHP',
              },
              value: this.rhp, // Value of where the line will appear
              width: 2,
              zIndex: 3, // Width of the line
            },
          ],
        },
        yAxis: {
          tickInterval: this.isMop ? 1 : 5,
          title: {
            text: this.isMop
              ? this.$t('productDetails.performanceChartLabels.investmentOptionsFrequency')
              : this.$t('productDetails.performanceChartLabels.confidenceInterval'),
          },
          allowDecimals: false,
          min: this.isMop ? null : 50,
          max: this.isMop ? null : 95,
        },
        credits: {
          enabled: false,
        },
        title: {
          text: null,
        },
        series: [
          {
            data: this.isMop
              ? this.breakEven['confidence' + this.breakEventPercentage]
                  .map((el) => [el.costBreakEven, el.no])
                  .sort((el1, el2) => (el1[0] > el2[0] ? 1 : -1))
              : this.breakEven.map((el) => [el.breakEvenRHP, el.confidenceInterval]).sort(this.sortPoints),
          },
        ],
      }
    },
    setOptions() {
      this.hcKey += 1
      return setChartMenuItemsLang()
    },
  },
}
</script>
<style lang="scss" scoped></style>
